import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as admin from '../../../admin'

import * as gql from './gql'
import * as models from './models'
import type { Context, TypeParams } from './types'

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link admin.editingVariables.Feature} */
  private readonly _editingVariablesFeature = ngCore.inject(
    admin.editingVariables.Feature,
  )

  /* ---------------------------- Standard methods ---------------------------- */

  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => (context ? gql.queries.pages.execute(context) : rxjs.of([])),
  )

  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      // Query, content and variables are not very useful to present in most contexts
      // where attributes are used
      ec.models.Attribute.fromKey<TypeParams>('id', { name: 'ID' }),
      ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
      ec.models.Attribute.fromKey<TypeParams>('description', {
        name: 'Description',
      }),
    ])

  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.queries.page.executor({ ...context, identifier: { id } })
  }

  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    TypeAsserter.assertIsNotNullish(context)
    const variables =
      this._editingVariablesFeature.selectors.definitionsList.asSignal()
    input = {
      ...input,
      variables,
    }
    return gql.mutations.createPage.executor({
      ...context,
      input: gql.inputs.page.INPUT.getGqlFrom(input),
    })
  }

  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    const variables =
      this._editingVariablesFeature.selectors.definitionsList.asSignal()
    updates = {
      ...updates,
      variables,
    }
    return gql.mutations.updatePage.executor({
      ...context,
      identifier: { id },
      updates: gql.inputs.page.UPDATES.getGqlFrom(updates),
    })
  }

  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.deletePage.executor({ ...context, identifier: { id } })
  }

  /* ------------------------------ Extra methods ----------------------------- */

  /**
   * Retrieves the {@link models.widget.Preview} for the given
   * {@link ents.core.common.FlexibleIdentifier}
   */
  public preview(
    context: Context,
    {
      flexibleIdentifier,
    }: {
      readonly flexibleIdentifier: ents.core.common.FlexibleIdentifier<ec.models.EntityId>
    },
  ): rxjs.Observable<models.widget.Preview | undefined> {
    TypeAsserter.assertIsNotNullish(context)
    return gql.queries.pageWidgetsPreview.execute({
      ...context,
      widgetIdentifier: flexibleIdentifier,
    })
  }
}
